import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Printer, ArrowLeft, CheckCircle2 } from "lucide-react";
import { InvoiceStatusBadge } from "../components/InvoiceStatusBadge";
import { ShortageReview } from "../components/ShortageReview";
import { CATEGORY_LABELS } from "../lib/categories";
import { groupByCategory } from "../lib/sections";

export default function InvoiceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const canMarkPaid = user?.role === "admin";
  const shortagePending = inv?.shortage_review?.status === "pending";
  const canSend = user?.role === "admin" && inv?.status === "draft";

  const [billAddr, setBillAddr] = useState("");
  const load = () => api.get(`/invoices/${id}`).then((r) => setInv(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    if (!inv?.location_id) return;
    api.get("/locations").then((r) => setBillAddr(r.data.find((l) => l.id === inv.location_id)?.address || "")).catch(() => {});
  }, [inv?.location_id]);

  // Set the tab title to the invoice number so "Save as PDF" uses a clean filename
  // instead of "Macaron by Patisse · Operating System".
  useEffect(() => {
    if (!inv) return;
    const prev = document.title;
    document.title = `${inv.invoice_no} — ${inv.customer || inv.location_name}`;
    return () => { document.title = prev; };
  }, [inv]);

  const togglePaid = async () => {
    if (!inv) return;
    setBusy(true);
    try {
      const next = inv.status === "paid" ? "pending" : "paid";
      await api.patch(`/invoices/${inv.id}/status`, { status: next });
      toast.success(next === "paid" ? "Invoice marked Paid" : "Reverted to Pending");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const sendInvoice = async () => {
    if (!inv) return;
    setBusy(true);
    try {
      await api.post(`/invoices/${inv.id}/send`);
      toast.success("Invoice sent to franchisee");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!inv) return <Layout><div className="text-muted-foreground">Loading…</div></Layout>;

  const taxPct = ((inv.tax_rate ?? 0) * 100).toFixed(2);

  return (
    <Layout>
      <div className="print-only-target">
      <div className="mb-6 flex items-center justify-between no-print">
        <Link to="/invoices" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to invoices
        </Link>
        <div className="flex items-center gap-2">
          {canSend && (
            <button
              onClick={sendInvoice}
              disabled={busy || shortagePending}
              title={shortagePending ? "Resolve the Kitchen shortage first" : undefined}
              data-testid="send-invoice-btn"
              className="h-11 px-4 rounded-xl text-sm font-medium bg-foreground text-background hover:bg-foreground/90 inline-flex items-center gap-2 disabled:opacity-60"
            >
              <CheckCircle2 className="h-4 w-4" /> Approve & Send to Franchisee
            </button>
          )}
          {canMarkPaid && inv.status !== "draft" && (
            <button
              onClick={togglePaid}
              disabled={busy}
              data-testid="toggle-paid-btn"
              className={`h-11 px-4 rounded-xl text-sm font-medium inline-flex items-center gap-2 ${
                inv.status === "paid"
                  ? "border border-border bg-card hover:bg-secondary"
                  : "border border-[#2E7D32]/30 text-[#2E7D32] hover:bg-[#E8F2EA]"
              }`}
            >
              <CheckCircle2 className="h-4 w-4" />
              {inv.status === "paid" ? "Revert to Pending" : "Mark Paid"}
            </button>
          )}
          <button
            onClick={() => window.print()}
            data-testid="print-invoice-btn"
            className="h-11 px-5 rounded-xl bg-foreground text-background font-medium text-sm inline-flex items-center gap-2 hover:bg-foreground/90"
          >
            <Printer className="h-4 w-4" /> Print / Save as PDF
          </button>
        </div>
      </div>

      {user?.role === "admin" && <ShortageReview inv={inv} onResolved={load} />}
      <article className="luxury-card max-w-3xl mx-auto p-10 sm:p-14 print-page print-target" data-testid="invoice-page">
        <header className="flex items-start justify-between mb-12">
          <div data-testid="invoice-from">
            <div className="font-display text-2xl font-medium tracking-tight">Hafizo LLC</div>
            <div className="text-xs text-muted-foreground mt-2 max-w-[14rem]">
              2033 West Gray St<br />Houston, TX 77019
            </div>
          </div>
          <div className="text-right">
            <div className="label-eyebrow">Invoice</div>
            <div className="font-display text-2xl font-medium tracking-tight">{inv.invoice_no}</div>
            <div className="mt-2 flex items-center justify-end gap-2">
              <InvoiceStatusBadge status={inv.status} />
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              Issued {new Date(inv.created_at).toLocaleDateString()}
              {inv.paid_at && <> · Paid {new Date(inv.paid_at).toLocaleDateString()}</>}
            </div>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-6 mb-10">
          <div>
            <div className="label-eyebrow mb-2">Billed To</div>
            <div className="font-medium" data-testid="invoice-bill-to">{inv.customer || inv.location_name}</div>
            {inv.customer && (
              <div className="text-xs text-muted-foreground">{inv.location_name}</div>
            )}
            {billAddr && <div className="text-xs text-muted-foreground mt-0.5" data-testid="invoice-bill-to-address">{billAddr}</div>}
          </div>
          <div>
            <div className="label-eyebrow mb-2">Order</div>
            <div className="font-medium">{inv.order_no || "Manual invoice"}</div>
            <div className="text-xs text-muted-foreground">Issued by {inv.issued_by}</div>
          </div>
        </section>

        <table className="w-full text-sm mb-8">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 label-eyebrow font-medium">Item</th>
              <th className="text-right py-2 label-eyebrow font-medium">Qty</th>
              <th className="text-right py-2 label-eyebrow font-medium">Unit</th>
              <th className="text-right py-2 label-eyebrow font-medium">Total</th>
            </tr>
          </thead>
          {groupByCategory(inv.items).map((g) => (
          <tbody key={g.cat} data-testid={`invoice-section-${g.cat}`}>
            <tr><td colSpan="4" className="pt-5 pb-1.5 label-eyebrow text-[10px] text-[hsl(var(--primary))]">{g.label}</td></tr>
            {g.items.map((it) => {
              const idx = it._idx;
              const catLabel = CATEGORY_LABELS[it.category] || it.category;
              return (
                <tr key={`${inv.invoice_no}-${it.name}-${idx}`} className="border-b border-border">
                  <td className="py-3">
                    <div className="font-medium" data-testid={`invoice-line-name-${idx}`}>
                      {it.name}{catLabel && it.category !== "other" ? <span className="text-muted-foreground font-normal"> — {catLabel}</span> : null}
                    </div>
                  </td>
                  <td className="py-3 text-right">{it.billed_quantity ?? it.quantity}</td>
                  <td className="py-3 text-right">${it.unit_price.toFixed(2)}</td>
                  <td className="py-3 text-right font-medium">${it.line_total.toFixed(2)}</td>
                </tr>
              );
            })}
            <tr className="bg-secondary/40" data-testid={`invoice-section-total-${g.cat}`}>
              <td className="py-2 pl-2 text-xs font-medium">{g.label} total</td>
              <td className="py-2 text-right text-xs font-medium tabular-nums">{g.items.reduce((s, it) => s + (it.billed_quantity ?? it.quantity ?? 0), 0)}</td>
              <td />
              <td className="py-2 text-right text-xs font-semibold tabular-nums">${g.items.reduce((s, it) => s + (it.line_total || 0), 0).toFixed(2)}</td>
            </tr>
          </tbody>
          ))}
        </table>

        <div className="flex justify-end">
          <dl className="text-sm space-y-2 min-w-[18rem]">
            <Row label="Subtotal" value={inv.subtotal} />
            {(inv.discount_amount ?? 0) > 0 && (
              <Row
                label={`Discount${inv.discount_label ? ` · ${inv.discount_label}` : ""}`}
                value={-inv.discount_amount}
              />
            )}
            {(inv.tax ?? 0) > 0 && <Row label={`Tax (${taxPct}%)`} value={inv.tax} />}
            <div className="border-t border-border pt-2 flex justify-between">
              <dt className="font-display text-lg">Total</dt>
              <dd className="font-display text-lg">${inv.total.toFixed(2)}</dd>
            </div>
          </dl>
        </div>

        {inv.notes && (
          <div className="mt-10 pt-6 border-t border-border">
            <div className="label-eyebrow mb-1">Notes</div>
            <p className="text-sm italic text-muted-foreground">{inv.notes}</p>
          </div>
        )}

        <footer className="mt-14 pt-6 border-t border-border text-xs text-muted-foreground text-center print-hide">
          <div className="space-y-1">
            <div>Payment: <span className="text-foreground font-medium">Checks payable to Hafizo, LLC</span></div>
            <div>Terms: <span className="text-foreground font-medium">Net 30</span></div>
            <div className="pt-2 italic">Thank you for your business.</div>
          </div>
        </footer>
      </article>
      </div>
    </Layout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular-nums">${value.toFixed(2)}</dd>
    </div>
  );
}
